import { useMemo, useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { cn } from '@/lib/utils';
import {
  COMMON_TIME_ZONES,
  browserTimeZone,
  supportedTimeZones,
  timeZoneLabel,
  timeZoneOffsetLabel,
} from '../utils';

interface TimezonePickerProps {
  value: string;
  onChange: (zone: string) => void;
  id?: string;
}

/** How many search hits to render — enough to find anything, few enough to scroll. */
const MAX_RESULTS = 60;

/**
 * Timezone chooser: a short curated list by default, the full IANA database by
 * search.
 *
 * Replaces a plain `<Select>` over every zone the browser knows, which had two
 * problems. It listed ~450 entries with no way to filter, so picking "Pacific
 * Time" meant scrolling past a hundred `America/Indiana/*` aliases. And Select
 * anchors its list to the trigger, so near the top of the viewport the list was
 * clipped off-screen; a Popover flips to the other side instead.
 *
 * Curating what is SHOWN is a UI decision. Curating what is SELECTABLE would be
 * a correctness one — an event synced from Outlook can legitimately carry any
 * zone, and it must remain choosable — so search covers everything and the
 * event's current zone is always offered.
 */
export function TimezonePicker({ value, onChange, id }: TimezonePickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const local = browserTimeZone();

  /** Pinned above the rest: where the user is, and where this event already is. */
  const pinned = useMemo(() => {
    const zones = [local, value].filter((z, i, a) => z && a.indexOf(z) === i);
    return zones;
  }, [local, value]);

  const results = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return null; // Not searching — show the curated groups.
    return supportedTimeZones()
      .filter((zone) => {
        // Match the IANA id AND the friendly label, so both "Los_Angeles" and
        // "Pacific" find the same entry.
        return (
          zone.toLowerCase().includes(query) || timeZoneLabel(zone).toLowerCase().includes(query)
        );
      })
      .slice(0, MAX_RESULTS);
  }, [search]);

  const commit = (zone: string) => {
    onChange(zone);
    setSearch('');
    setOpen(false);
  };

  const row = (zone: string) => (
    <CommandItem
      key={zone}
      // cmdk filters on `value`; we already filtered, so make every rendered
      // row match by giving it a stable unique value and disabling the built-in
      // filter on <Command> below.
      value={zone}
      onSelect={() => commit(zone)}
      className="flex items-center gap-2"
    >
      <Check className={cn('h-4 w-4 shrink-0', zone === value ? 'opacity-100' : 'opacity-0')} />
      <span className="flex-1 truncate">{timeZoneLabel(zone)}</span>
      <span className="text-xs text-muted-foreground shrink-0 tabular-nums">
        {timeZoneOffsetLabel(zone)}
      </span>
    </CommandItem>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          id={id}
          type="button"
          role="combobox"
          aria-expanded={open}
          className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
        >
          <span className="truncate">
            {timeZoneLabel(value)}
            <span className="text-muted-foreground"> · {timeZoneOffsetLabel(value)}</span>
          </span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      {/* Radix flips and shifts this to stay on screen, which the Select it
          replaced could not do near the top of the viewport. */}
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Search timezones…" value={search} onValueChange={setSearch} />
          <CommandList>
            <CommandEmpty>No timezone found.</CommandEmpty>
            {results ? (
              <CommandGroup>{results.map(row)}</CommandGroup>
            ) : (
              <>
                <CommandGroup heading="Current">{pinned.map(row)}</CommandGroup>
                <CommandGroup heading="Common">
                  {COMMON_TIME_ZONES.filter((t) => !pinned.includes(t.zone)).map((t) =>
                    row(t.zone),
                  )}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
